/**
 * Chart Models - DTOs for Chart API endpoints
 */

/**
 * Granularity enum for chart data grouping
 * 0 = Daily, 1 = Weekly, 2 = Monthly
 */
export enum ChartGranularity {
  Daily = 0,
  Weekly = 1,
  Monthly = 2
}

/**
 * Represents a single data point in a chart
 * Supports multiple property name formats from API
 */
export interface ChartDataPointDto {
  // Primary format
  label?: string;
  value?: number;
  // Alternative format (date-based)
  date?: string;
  count?: number;
  // Alternative format (period-based)
  period?: string;
  total?: number;
  // Alternative format (timestamp)
  timestamp?: string;
  // Any other properties
  [key: string]: string | number | undefined;
}

/**
 * Entity chart data containing data points for a single entity
 */
export interface EntityChartDataDto {
  entityName: string;
  totalCount: number;
  dataPoints: ChartDataPointDto[];
}

/**
 * Cumulative chart data containing multiple entity data
 */
export interface CumulativeChartDataDto {
  startDate: string;
  endDate: string;
  granularity: ChartGranularity;
  entityData: EntityChartDataDto[];
}

/**
 * Chart summary data
 */
export interface ChartSummaryDto {
  totalAppointments?: number;
  totalCollections?: number;
  totalCustomers?: number;
  totalAgents?: number;
  totalRevenue?: number;
  [key: string]: number | undefined;
}

/**
 * Filter parameters for chart API requests
 */
export interface ChartFilterParams {
  startDate?: string;
  endDate?: string;
  granularity?: ChartGranularity;
  geoZoneIds?: string;
  agentIds?: string;
}

/**
 * Available entity types for the Chart/{entityType} endpoint
 */
export type ChartEntityType = 
  | 'appointments'
  | 'collections'
  | 'customers'
  | 'revenue'
  | 'agents';

/**
 * Chart configuration for reusable chart components
 */
export interface ChartConfig {
  title: string;
  entityType?: ChartEntityType;
  chartType: 'line' | 'bar' | 'area' | 'pie' | 'doughnut';
  colorScheme?: string[];
  showLegend?: boolean;
  showDataLabels?: boolean;
  height?: number;
}

/**
 * Preset date range options for quick filtering
 */
export type DateRangePreset = 
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'thisYear'
  | 'custom';

/**
 * Problem details for API error responses
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: any;
}

/**
 * Insights summary with totals
 */
export interface InsightsSummaryDto {
  totalCollections: number;
  totalGrandTotal: number;
  totalPlatformCharges: number;
  totalCollectionCharges: number;
  totalScrapValue: number;
}

/**
 * Insights data point with daily and cumulative values
 */
export interface InsightsChartDataPointDto {
  date: string;
  // Daily values
  collectionCount: number;
  grandTotal: number;
  platformCharges: number;
  collectionCharges: number;
  scrapValue: number;
  // Cumulative values
  cumulativeGrandTotal: number;
  cumulativePlatformCharges: number;
  cumulativeCollectionCharges: number;
  cumulativeScrapValue: number;
  cumulativeCollectionCount: number;
}

/**
 * Cumulative insights chart data
 */
export interface CumulativeInsightsChartDataDto {
  startDate: string;
  endDate: string;
  granularity: ChartGranularity;
  summary: InsightsSummaryDto;
  dataPoints: InsightsChartDataPointDto[];
}

